import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeAwareFloatingIcons } from '@/components/ui/theme-aware-icons';
import { cn } from '@/lib/utils';

interface HeroSectionProps {
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ className }) => {
  const scrollToSection = (sectionId: string): void => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const downloadResume = (): void => {
    // Open resume in new tab for better UX
    window.open('/resume.pdf', '_blank', 'noopener,noreferrer');
  };

  const techStack = [
    'Java',
    'Kotlin',
    'Spring Boot',
    'React',
    'TypeScript',
    'Python',
    'OpenAI APIs',
    'AWS',
    'Docker',
    'Kubernetes',
    'PostgreSQL',
    'GraphQL',
  ];

  return (
    <section
      id='hero'
      className={cn(
        'min-h-screen flex items-center justify-center bg-background',
        className
      )}
    >
      <div className='container mx-auto px-4 py-16'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
          {/* Content */}
          <div className='space-y-8'>
            {/* Status Badge */}
            <div className='flex items-center space-x-2'>
              <Badge variant='secondary' className='text-sm'>
                <span className='w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse'></span>
                Available for new opportunities
              </Badge>
            </div>

            {/* Main Heading */}
            <div className='space-y-4'>
              <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight'>
                Senior Software
                <br />
                <span className='text-primary'>Engineer</span>
              </h1>
              <p className='text-xl text-muted-foreground max-w-2xl'>
                Passionate about building scalable solutions, leading
                high-performing teams, and driving technical innovation. 9+
                years of experience in full-stack development and system
                architecture.
              </p>
            </div>

            {/* Tech Stack */}
            <div className='space-y-3'>
              <p className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
                Tech Stack
              </p>
              <div className='flex flex-wrap gap-2'>
                {techStack.map(tech => (
                  <Badge key={tech} variant='outline' className='text-sm'>
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Call to Action Buttons */}
            <div className='flex flex-col sm:flex-row gap-4'>
              <Button
                size='lg'
                onClick={() => scrollToSection('contact')}
                className='text-base px-8'
              >
                Get In Touch
              </Button>
              <Button
                variant='outline'
                size='lg'
                onClick={downloadResume}
                className='text-base px-8'
              >
                View Resume
              </Button>
            </div>

            {/* Quick Stats */}
            <div className='grid grid-cols-3 gap-6 pt-8 border-t'>
              <div className='text-center sm:text-left'>
                <div className='text-2xl font-bold text-foreground'>9+</div>
                <div className='text-sm text-muted-foreground'>
                  Years Experience
                </div>
              </div>
              <div className='text-center sm:text-left'>
                <div className='text-2xl font-bold text-foreground'>50+</div>
                <div className='text-sm text-muted-foreground'>
                  Projects Delivered
                </div>
              </div>
              <div className='text-center sm:text-left'>
                <div className='text-2xl font-bold text-foreground'>15+</div>
                <div className='text-sm text-muted-foreground'>
                  Team Members Led
                </div>
              </div>
            </div>
          </div>

          {/* Profile Image Placeholder */}
          <div className='flex justify-center lg:justify-end'>
            <div className='relative'>
              {/* Profile Image */}
              <div className='w-80 h-80 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 dark:from-primary/30 dark:to-secondary/30 flex items-center justify-center border-4 border-background shadow-2xl dark:shadow-primary/10 transition-all duration-300 overflow-hidden'>
                <img
                  src='/MarinoJeremy_Profilepic.png'
                  alt='Jeremy Marino - Senior Software Engineer'
                  className='w-full h-full object-cover rounded-full'
                />
              </div>

              {/* Theme-Aware Floating Elements */}
              <ThemeAwareFloatingIcons />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className='flex justify-center mt-16'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => scrollToSection('about')}
            className='flex flex-col items-center space-y-2 text-muted-foreground hover:text-foreground'
          >
            <span className='text-sm'>Scroll to explore</span>
            <ChevronDown className='w-4 h-4 animate-bounce' />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
